import { Injectable, inject } from '@angular/core';
import { offerConverter, saveComponentConverter } from '@seu-converter';
import {
  OfferFormService,
  RequestService,
  StoreService,
  ToastService,
} from '@seu-services';
import {
  SEU_API_ENDPOINTS,
  SeuComponent,
  SeuComponentBase,
  SeuInterconnection,
  SeuOffer,
  SeuOfferApi,
} from '@seu-types';
import { catchError, firstValueFrom, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ComponentService {
  private readonly offerFormService = inject(OfferFormService);
  private readonly store = inject(StoreService);
  private readonly requestService = inject(RequestService);
  private readonly toastService = inject(ToastService);

  public async createComponent(
    offer: SeuOffer,
    interconnectionId: string,
    data: SeuComponentBase
  ): Promise<string | undefined> {
    const endpoint = SEU_API_ENDPOINTS.filterComponent
      .replace(':offerId', offer.id)
      .replace(':interconnectionId', interconnectionId);

    const dataApi = saveComponentConverter.toApi(data);

    const responseData = await firstValueFrom(
      this.requestService
        .post<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS, dataApi)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'components.toasts.create-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);

      const originalInterconnection = offer.interconnections.find(
        (item) => item.id === interconnectionId
      );
      const updatedInterconnection = updatedOffer.interconnections.find(
        (item) => item.id === interconnectionId
      );

      this.store.setActiveOffer(updatedOffer);
      this.store.updateActiveInterconnection(updatedInterconnection ?? null);
      this.offerFormService.init(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'components.toasts.create-success',
      // });

      const addedComponent = updatedInterconnection?.components.find((item) => {
        if (
          !originalInterconnection?.components.find(
            (originalItem) => originalItem.id === item.id
          )
        ) {
          return item;
        }
        return undefined;
      });

      return addedComponent?.id;
    }

    return undefined;
  }

  public getInterconnectionByComponentId(
    offer: SeuOffer,
    componentId: string
  ): SeuInterconnection | undefined {
    let interconnection = undefined;

    offer.interconnections.forEach((ic) => {
      ic.components.forEach((item) => {
        if (item.id === componentId) {
          interconnection = ic;
        }
      });
    });

    return interconnection;
  }

  public getComponentFromOffer(
    offer: SeuOffer,
    componentId: string
  ): SeuComponent | undefined {
    let component = undefined;

    offer.interconnections.forEach((interconnection) => {
      interconnection.components.forEach((item) => {
        if (item.id === componentId) {
          component = item;
        }
      });
    });

    return component;
  }

  public async deleteComponent(
    offerId: string,
    interconnectionId: string,
    componentId: string
  ): Promise<void> {
    const endpoint = SEU_API_ENDPOINTS.filterComponentDetail
      .replace(':offerId', offerId)
      .replace(':interconnectionId', interconnectionId)
      .replace(':componentId', componentId);

    const responseData = await firstValueFrom(
      this.requestService
        .delete<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'components.toasts.delete-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.store.updateActiveInterconnection(
        updatedOffer.interconnections.find(
          (item) => item.id === interconnectionId
        ) ?? null
      );
      this.offerFormService.init(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'components.toasts.delete-success',
      // });
    }
  }

  public async updateComponent(
    offerId: string,
    interconnectionId: string,
    componentId: string,
    data: SeuComponentBase
  ): Promise<void> {
    const endpoint = SEU_API_ENDPOINTS.filterComponentDetail
      .replace(':offerId', offerId)
      .replace(':interconnectionId', interconnectionId)
      .replace(':componentId', componentId);

    const dataApi = saveComponentConverter.toApi(data);

    const responseData = await firstValueFrom(
      this.requestService
        .put<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS, dataApi)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'components.toasts.save-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.store.updateActiveInterconnection(
        updatedOffer.interconnections.find(
          (item) => item.id === interconnectionId
        ) ?? null
      );
      this.offerFormService.init(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'components.toasts.save-success',
      // });
    }
  }

  public isComponentPlacedOnCanvas(
    offer: SeuOffer,
    componentId: string
  ): boolean {
    const interconnection = this.getInterconnectionByComponentId(
      offer,
      componentId
    );

    if (!interconnection) {
      return false;
    }

    return !!interconnection.canvas.components.find(
      (component) => component.componentId === componentId
    );
  }
}
